import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Form, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatCustomerDisplayName } from '../utils/customerDisplayName';
import MoneyCards, { moneyAccess } from './MoneyCards';
import ModuleAlerts from './ModuleAlerts';
import SetupChecklist from './SetupChecklist';

import { formatDate } from '../utils/dateFormat';
// Validated (see dataviz skill: scripts/validate_palette.js) against both surfaces —
// light mode checked at #fcfcfb, dark mode at #1a1a19. Each chart is its own single-series
// panel, so these don't need to double as a shared legend, but keeping them mutually
// CVD-distinguishable is a cheap and helpful diagnostic when the charts are used side by side.
const CHART_COLORS = {
  light: { sales: '#0d6efd', purchase: '#c2610a', stock: '#6f42c1', grid: '#e9ecef', axis: '#6c757d' },
  dark: { sales: '#3d8bfd', purchase: '#c97a3d', stock: '#a370f7', grid: '#3a3a38', axis: '#adb5bd' }
};

const TIMEFRAME_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 12 months', days: 365 }
];

// Same six stages, order, and labels as the "Dock to Ledger" guide
// (public/guide/getting-started.html), so the two stay in sync.
const ONBOARDING_STEPS = [
  { label: 'Set up', path: '/org-admin' },
  { label: 'Purchase', path: '/purchase' },
  { label: 'Receive & grade', path: '/inventory' },
  { label: 'Process', path: '/production' },
  { label: 'Sell', path: '/sales' },
  { label: 'Books', path: '/accounts' }
];

function formatShortDate(isoDate) {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const colors = CHART_COLORS[theme] || CHART_COLORS.light;

  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCustomers: 0,
    totalOrders: 0,
    totalSaleBills: 0,
    recentOrders: []
  });
  const [organizationCurrency, setOrganizationCurrency] = useState('USD');
  const [timeframeDays, setTimeframeDays] = useState(30);
  const [salesTrend, setSalesTrend] = useState([]);
  const [purchaseTrend, setPurchaseTrend] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  const formatCurrency = useCallback((amount) => {
    const value = parseFloat(amount);
    if (Number.isNaN(value)) return '-';
    return `${organizationCurrency} ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [organizationCurrency]);

  useEffect(() => {
    fetchDashboardData();
    fetchOrganizationCurrency();
  }, []);

  useEffect(() => {
    fetchChartData(timeframeDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframeDays]);

  const fetchOrganizationCurrency = async () => {
    try {
      if (user?.organizationId) {
        const response = await axios.get(`/api/organizations/${user.organizationId}`);
        setOrganizationCurrency(response.data?.defaultCurrency || 'USD');
      }
    } catch (error) {
      console.error('Error fetching organization currency:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [productsRes, customersRes, ordersRes, saleBillsRes] = await Promise.all([
        axios.get('/api/products').catch(() => ({ data: [] })),
        axios.get('/api/customers').catch(() => ({ data: [] })),
        axios.get('/api/orders').catch(() => ({ data: [] })),
        axios.get('/api/sale-bills').catch(() => ({ data: [] }))
      ]);

      const products = Array.isArray(productsRes.data) ? productsRes.data : [];
      const customers = Array.isArray(customersRes.data) ? customersRes.data : [];
      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const saleBills = Array.isArray(saleBillsRes.data) ? saleBillsRes.data : [];

      setStats({
        totalProducts: products.length,
        totalCustomers: customers.length,
        totalOrders: orders.length,
        totalSaleBills: saleBills.length,
        recentOrders: orders.slice(0, 5)
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats(prev => ({
        ...prev,
        recentOrders: Array.isArray(prev.recentOrders) ? prev.recentOrders : []
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (days) => {
    try {
      setChartsLoading(true);
      const [salesRes, purchaseRes, stockRes] = await Promise.all([
        axios.get('/api/dashboard/sales-trend', { params: { days } }).catch(() => ({ data: [] })),
        axios.get('/api/dashboard/purchase-trend', { params: { days } }).catch(() => ({ data: [] })),
        axios.get('/api/inventory/stock-levels').catch(() => ({ data: [] }))
      ]);

      setSalesTrend((Array.isArray(salesRes.data) ? salesRes.data : []).map((p) => ({
        date: p.date,
        label: formatShortDate(p.date),
        total: parseFloat(p.total) || 0
      })));
      setPurchaseTrend((Array.isArray(purchaseRes.data) ? purchaseRes.data : []).map((p) => ({
        date: p.date,
        label: formatShortDate(p.date),
        total: parseFloat(p.total) || 0
      })));

      const stockRows = Array.isArray(stockRes.data) ? stockRes.data : [];
      const byItem = new Map();
      for (const row of stockRows) {
        const qty = parseFloat(row.quantity) || 0;
        const key = row.itemName || 'Unknown';
        const existing = byItem.get(key);
        if (existing) {
          existing.quantity += qty;
        } else {
          byItem.set(key, { itemName: key, quantity: qty, uom: row.uom || '' });
        }
      }
      const topItems = Array.from(byItem.values())
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10);
      setStockSummary(topItems);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  const salesTotal = salesTrend.reduce((sum, p) => sum + p.total, 0);
  const purchaseTotal = purchaseTrend.reduce((sum, p) => sum + p.total, 0);
  const isNewOrg = stats.totalProducts === 0 && stats.totalCustomers === 0
    && stats.totalOrders === 0 && stats.totalSaleBills === 0;

  const chartTooltipStyle = {
    backgroundColor: theme === 'dark' ? '#242423' : '#ffffff',
    border: `1px solid ${colors.grid}`,
    borderRadius: 6,
    fontSize: 13
  };

  return (
    <Container fluid>
      {isNewOrg && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Welcome to iBigDo</h5>
                <p className="text-muted mb-0">
                  Follow one purchase all the way to the ledger to see how the six stages fit together.
                </p>
              </div>
              <Link to="/help/getting-started" className="btn btn-sm btn-outline-primary text-nowrap">
                <i className="bi bi-book me-1" />
                Getting Started Guide
              </Link>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {ONBOARDING_STEPS.map((step, i) => (
                <React.Fragment key={step.path}>
                  <Link to={step.path} className="text-decoration-none">
                    <Badge bg="light" text="dark" className="border px-3 py-2 fw-normal">
                      {i + 1}. {step.label}
                    </Badge>
                  </Link>
                  {i < ONBOARDING_STEPS.length - 1 && (
                    <i className="bi bi-arrow-right text-muted" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      <MoneyCards user={user} currency={organizationCurrency} />

      {/* Counts, for those who don't see the money cards */}
      {!Object.values(moneyAccess(user)).some(Boolean) && (
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.totalProducts}</h3>
              <p className="text-muted">Total Products</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.totalCustomers}</h3>
              <p className="text-muted">Total Customers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{stats.totalOrders}</h3>
              <p className="text-muted">Total Orders</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.totalSaleBills}</h3>
              <p className="text-muted">Total Sale Bills</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      )}

      <SetupChecklist />
      <ModuleAlerts module="dashboard" />

      {/* Timeframe selector */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-end align-items-center gap-2">
          <Form.Label className="mb-0 text-muted">Timeframe:</Form.Label>
          <Form.Select
            style={{ width: 'auto' }}
            value={timeframeDays}
            onChange={(e) => setTimeframeDays(parseInt(e.target.value, 10))}
          >
            {TIMEFRAME_OPTIONS.map((opt) => (
              <option key={opt.days} value={opt.days}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Trend + Stock Charts */}
      <Row className="mb-4">
        <Col md={6} className="mb-3 mb-md-0">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Sales</h5>
              <span className="text-muted small">{formatCurrency(salesTotal)} total</span>
            </Card.Header>
            <Card.Body>
              {chartsLoading ? (
                <div className="d-flex justify-content-center py-5"><Spinner animation="border" size="sm" /></div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={salesTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.axis }} axisLine={{ stroke: colors.grid }} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value) => [formatCurrency(value), 'Sales']}
                      labelStyle={{ color: colors.axis }}
                    />
                    <Line type="monotone" dataKey="total" stroke={colors.sales} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Purchases</h5>
              <span className="text-muted small">{formatCurrency(purchaseTotal)} total</span>
            </Card.Header>
            <Card.Body>
              {chartsLoading ? (
                <div className="d-flex justify-content-center py-5"><Spinner animation="border" size="sm" /></div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={purchaseTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.axis }} axisLine={{ stroke: colors.grid }} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value) => [formatCurrency(value), 'Purchases']}
                      labelStyle={{ color: colors.axis }}
                    />
                    <Line type="monotone" dataKey="total" stroke={colors.purchase} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Stock Levels — Top Items</h5>
            </Card.Header>
            <Card.Body>
              {chartsLoading ? (
                <div className="d-flex justify-content-center py-5"><Spinner animation="border" size="sm" /></div>
              ) : stockSummary.length === 0 ? (
                <p className="text-muted text-center mb-0">No stock data available</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stockSummary} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis
                      dataKey="itemName"
                      tick={{ fontSize: 12, fill: colors.axis }}
                      axisLine={{ stroke: colors.grid }}
                      tickLine={false}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value, name, props) => [`${value.toLocaleString('en-US', { maximumFractionDigits: 3 })} ${props.payload.uom || ''}`.trim(), 'Quantity']}
                      labelStyle={{ color: colors.axis }}
                    />
                    <Bar dataKey="quantity" fill={colors.stock} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Orders */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent Orders</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Order Number</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentOrders && Array.isArray(stats.recentOrders) && stats.recentOrders.length > 0 ? (
                    stats.recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.orderNumber}</td>
                        <td>{formatCustomerDisplayName(order.customer)}</td>
                        <td>{formatDate(order.orderDate)}</td>
                        <td>
                          <span className={`badge ${
                            order.orderStatus === 'DELIVERED' ? 'bg-success' :
                            order.orderStatus === 'PENDING' ? 'bg-warning' :
                            order.orderStatus === 'CANCELLED' ? 'bg-danger' :
                            'bg-info'
                          }`}>
                            {order.orderStatus}
                          </span>
                        </td>
                        <td>${order.totalAmount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No recent orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
